/**
 * Usuarios: quién puede entrar al sistema. Vive en Configuración.
 *
 * Hasta ahora no había forma de crear una cuenta desde la app (solo la primera,
 * en /instalar), y los socios compartían la de uno. Acá cada administrador
 * tiene la suya.
 *
 * Sin roles: todas las cuentas son de administrador, y la pantalla no ofrece
 * elegir. Las bajas no borran: desactivan, porque la caja y el stock guardan
 * quién hizo cada movimiento. Dos reglas las pone el backend y la pantalla las
 * anticipa: nadie se da de baja a sí mismo, y siempre queda alguien activo.
 */
import { useState } from "react";
import { KeyRound, UserMinus, UserPlus, UserRoundCheck, Users } from "lucide-react";

import { Aviso } from "@/components/ui/aviso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { InputPassword } from "@/components/ui/input-password";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { ErrorApi, mensajeDeError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { mostrarExito } from "@/lib/errores";
import { useActualizarUsuario, useCrearUsuario, useUsuarios } from "@/lib/queries";
import type { FilaUsuario } from "@/lib/tipos";
import { formatearFecha } from "@/lib/utils";

/** Mismo mínimo que el backend y que /instalar. */
export const MINIMO_PASSWORD = 8;

const MAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SeccionUsuarios(): JSX.Element {
  const { data, isLoading, isError, error } = useUsuarios();
  const { usuario: yo } = useAuth();

  const [agregando, setAgregando] = useState(false);
  const [cambiandoClave, setCambiandoClave] = useState<FilaUsuario | null>(null);
  const [cambiandoEstado, setCambiandoEstado] = useState<FilaUsuario | null>(null);

  const usuarios = data?.filas ?? [];
  const activos = usuarios.filter((u) => u.activo).length;

  return (
    <Card as="section" padding="none" aria-labelledby="titulo-usuarios">
      <div className="border-b px-4 py-3">
        <TituloSeccion
          nivel="seccion"
          icono={Users}
          descripcion="Cada uno entra con su mail y su contraseña. Todos son administradores."
          acciones={
            <Button variant="outline" size="sm" onClick={() => setAgregando(true)}>
              <UserPlus aria-hidden="true" />
              Agregar usuario
            </Button>
          }
        >
          <span id="titulo-usuarios">Usuarios</span>
        </TituloSeccion>
      </div>

      {isLoading ? (
        <div className="p-4">
          <Cargando />
        </div>
      ) : isError ? (
        <div className="p-4">
          <Aviso tono="destructive">{mensajeDeError(error)}</Aviso>
        </div>
      ) : (
        <ul className="divide-y">
          {usuarios.map((u) => {
            const esYo = u.id === yo?.id;
            // La baja se ofrece solo si queda otro activo y no es la cuenta
            // propia: el backend igual lo rechaza, pero un botón que siempre
            // falla es peor que no tenerlo.
            const puedeDarDeBaja = u.activo && !esYo && activos > 1;
            return (
              <li
                key={u.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                aria-label={u.nombre}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={
                        u.activo
                          ? "text-sm font-medium text-foreground"
                          : "text-sm font-medium text-muted-foreground"
                      }
                    >
                      {u.nombre}
                    </span>
                    {esYo ? <Badge variant="info">Vos</Badge> : null}
                    {!u.activo ? <Badge variant="outline">De baja</Badge> : null}
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {u.email} · desde el {formatearFecha(u.created_at)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {u.activo ? (
                    <Button variant="outline" size="sm" onClick={() => setCambiandoClave(u)}>
                      <KeyRound aria-hidden="true" />
                      Cambiar contraseña
                    </Button>
                  ) : null}
                  {puedeDarDeBaja ? (
                    <Button variant="outline" size="sm" onClick={() => setCambiandoEstado(u)}>
                      <UserMinus aria-hidden="true" />
                      Dar de baja
                    </Button>
                  ) : null}
                  {!u.activo ? (
                    <Button variant="outline" size="sm" onClick={() => setCambiandoEstado(u)}>
                      <UserRoundCheck aria-hidden="true" />
                      Reactivar
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <DialogAgregar abierto={agregando} onCerrar={() => setAgregando(false)} />
      <DialogCambiarClave
        usuario={cambiandoClave}
        esYo={cambiandoClave?.id === yo?.id}
        onCerrar={() => setCambiandoClave(null)}
      />
      <DialogEstado usuario={cambiandoEstado} onCerrar={() => setCambiandoEstado(null)} />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Agregar
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Alta en dos pasos: los datos y después un resumen que dice lo que implica
 * (entra con ese mail y puede hacer todo). El mail repetido lo rechaza el
 * backend; ese error vuelve al primer paso, debajo del campo.
 */
function DialogAgregar({
  abierto,
  onCerrar,
}: {
  abierto: boolean;
  onCerrar: () => void;
}): JSX.Element {
  const crear = useCrearUsuario();
  const [paso, setPaso] = useState<"datos" | "confirmar">("datos");
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [intento, setIntento] = useState(false);

  const errores = {
    nombre: nombre.trim() === "" ? "Poné un nombre." : undefined,
    email: !MAIL.test(email.trim()) ? "No es un mail válido." : undefined,
    password:
      password.length < MINIMO_PASSWORD ? `Mínimo ${MINIMO_PASSWORD} caracteres.` : undefined,
  };
  const valido = !errores.nombre && !errores.email && !errores.password;

  // El 409 del mail repetido (y cualquier error por campo del backend).
  const detalles = crear.error instanceof ErrorApi ? crear.error.detalles : {};
  const errorEmailServidor = detalles.email?.join(" ");

  function cerrar(): void {
    if (crear.isPending) return;
    setPaso("datos");
    setNombre("");
    setEmail("");
    setPassword("");
    setIntento(false);
    crear.reset();
    onCerrar();
  }

  function seguir(): void {
    setIntento(true);
    if (!valido) return;
    crear.reset();
    setPaso("confirmar");
  }

  function confirmar(): void {
    crear.mutate(
      { nombre: nombre.trim(), email: email.trim(), password },
      {
        onSuccess: () => {
          mostrarExito(`${nombre.trim()} ya puede entrar`, `Con ${email.trim().toLowerCase()}.`);
          cerrar();
        },
        onError: (e) => {
          if (e instanceof ErrorApi && Object.keys(e.detalles).length > 0) setPaso("datos");
        },
      },
    );
  }

  return (
    <Dialog
      open={abierto}
      onOpenChange={(a) => {
        if (!a) cerrar();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agregar usuario</DialogTitle>
          <DialogDescription>
            {paso === "datos" ? "Paso 1 de 2 · Los datos" : "Paso 2 de 2 · Revisá antes de crear"}
          </DialogDescription>
        </DialogHeader>

        {paso === "datos" ? (
          <form
            id="form-agregar-usuario"
            noValidate
            className="contents"
            onSubmit={(e) => {
              e.preventDefault();
              seguir();
            }}
          >
            <DialogBody>
              <Campo
                label="Nombre"
                error={detalles.nombre?.join(" ") ?? (intento ? errores.nombre : undefined)}
              >
                <Input
                  name="nombre"
                  autoComplete="off"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                />
              </Campo>
              <Campo
                label="Mail"
                error={errorEmailServidor ?? (intento ? errores.email : undefined)}
                ayuda="Es con lo que va a entrar."
              >
                <Input
                  type="email"
                  name="email"
                  autoComplete="off"
                  inputMode="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (crear.error) crear.reset();
                  }}
                />
              </Campo>
              <Campo
                label="Contraseña"
                error={detalles.password?.join(" ") ?? (intento ? errores.password : undefined)}
                ayuda={`Mínimo ${MINIMO_PASSWORD} caracteres. Pasásela en persona; la puede cambiar después.`}
              >
                <InputPassword
                  name="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Campo>
            </DialogBody>
          </form>
        ) : (
          <DialogBody className="space-y-3">
            <dl className="space-y-1 text-sm">
              <div className="flex gap-4">
                <dt className="w-16 shrink-0 text-muted-foreground">Nombre</dt>
                <dd className="min-w-0 break-words font-medium">{nombre.trim()}</dd>
              </div>
              <div className="flex gap-4">
                <dt className="w-16 shrink-0 text-muted-foreground">Mail</dt>
                <dd className="min-w-0 break-all font-medium">{email.trim().toLowerCase()}</dd>
              </div>
            </dl>
            <p className="text-sm text-muted-foreground">
              Va a poder entrar con ese mail y hacer todo lo que hacés vos: no hay permisos
              distintos.
            </p>
            {crear.error ? (
              <Aviso tono="destructive" aria-live="assertive">
                {mensajeDeError(crear.error)}
              </Aviso>
            ) : null}
          </DialogBody>
        )}

        <DialogFooter>
          {paso === "datos" ? (
            <>
              <Button type="button" variant="ghost" onClick={cerrar}>
                Cancelar
              </Button>
              <Button type="submit" form="form-agregar-usuario">
                Seguir
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPaso("datos")}
                disabled={crear.isPending}
              >
                Atrás
              </Button>
              <Button type="button" cargando={crear.isPending} onClick={confirmar}>
                Crear la cuenta
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cambiar contraseña
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Se puede cambiar la de cualquiera: es como se destraba a alguien que se la
 * olvidó. No pide la anterior por eso mismo.
 */
function DialogCambiarClave({
  usuario,
  esYo,
  onCerrar,
}: {
  usuario: FilaUsuario | null;
  esYo: boolean;
  onCerrar: () => void;
}): JSX.Element {
  const actualizar = useActualizarUsuario();
  const [password, setPassword] = useState("");
  const [intento, setIntento] = useState(false);

  const corta = password.length < MINIMO_PASSWORD;

  function cerrar(): void {
    if (actualizar.isPending) return;
    setPassword("");
    setIntento(false);
    actualizar.reset();
    onCerrar();
  }

  function confirmar(): void {
    setIntento(true);
    if (!usuario || corta) return;
    actualizar.mutate(
      { id: usuario.id, cambios: { password } },
      {
        onSuccess: () => {
          mostrarExito(
            esYo ? "Cambiaste tu contraseña" : `Cambiaste la contraseña de ${usuario.nombre}`,
            esYo ? "La próxima vez entrás con la nueva." : "Pasásela en persona.",
          );
          cerrar();
        },
      },
    );
  }

  return (
    <Dialog
      open={usuario !== null}
      onOpenChange={(a) => {
        if (!a) cerrar();
      }}
    >
      <DialogContent tamano="sm">
        <DialogHeader>
          <DialogTitle>{esYo ? "Cambiar tu contraseña" : "Cambiar la contraseña"}</DialogTitle>
          <DialogDescription>
            {usuario ? `${usuario.nombre} · ${usuario.email}` : ""}
          </DialogDescription>
        </DialogHeader>
        <form
          id="form-cambiar-clave"
          noValidate
          className="contents"
          onSubmit={(e) => {
            e.preventDefault();
            confirmar();
          }}
        >
          <DialogBody className="space-y-3">
            <Campo
              label="Contraseña nueva"
              error={intento && corta ? `Mínimo ${MINIMO_PASSWORD} caracteres.` : undefined}
              ayuda={
                esYo
                  ? "La sesión que tenés abierta sigue andando."
                  : "La anterior deja de servir en cuanto guardes."
              }
            >
              <InputPassword
                name="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Campo>
            {actualizar.error ? (
              <Aviso tono="destructive" aria-live="assertive">
                {mensajeDeError(actualizar.error)}
              </Aviso>
            ) : null}
          </DialogBody>
        </form>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={cerrar} disabled={actualizar.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form="form-cambiar-clave" cargando={actualizar.isPending}>
            Cambiar la contraseña
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dar de baja / Reactivar
// ─────────────────────────────────────────────────────────────────────────────

/** Confirmación de la baja (o de la vuelta). El mismo diálogo sirve para las dos. */
function DialogEstado({
  usuario,
  onCerrar,
}: {
  usuario: FilaUsuario | null;
  onCerrar: () => void;
}): JSX.Element {
  const actualizar = useActualizarUsuario();
  const baja = usuario?.activo ?? true;

  function cerrar(): void {
    if (actualizar.isPending) return;
    actualizar.reset();
    onCerrar();
  }

  function confirmar(): void {
    if (!usuario) return;
    actualizar.mutate(
      { id: usuario.id, cambios: { activo: !usuario.activo } },
      {
        onSuccess: () => {
          mostrarExito(
            baja ? `${usuario.nombre} quedó de baja` : `${usuario.nombre} puede volver a entrar`,
          );
          cerrar();
        },
      },
    );
  }

  return (
    <Dialog
      open={usuario !== null}
      onOpenChange={(a) => {
        if (!a) cerrar();
      }}
    >
      <DialogContent tamano="sm">
        <DialogHeader>
          <DialogTitle>
            {baja
              ? `¿Dar de baja a ${usuario?.nombre ?? ""}?`
              : `¿Reactivar a ${usuario?.nombre ?? ""}?`}
          </DialogTitle>
          <DialogDescription>{usuario?.email ?? ""}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {baja
              ? "No va a poder entrar más al sistema. Lo que cargó queda a su nombre, y se puede reactivar cuando quieras."
              : "Vuelve a entrar con su mail y la contraseña que tenía."}
          </p>
          {actualizar.error ? (
            <Aviso tono="destructive" aria-live="assertive">
              {mensajeDeError(actualizar.error)}
            </Aviso>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={cerrar} disabled={actualizar.isPending}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant={baja ? "destructive" : "default"}
            cargando={actualizar.isPending}
            onClick={confirmar}
          >
            {baja ? "Dar de baja" : "Reactivar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
